import React, { useState, useRef, useEffect } from 'react'
import { FormControl } from 'react-bootstrap'
import '../style/editableDropdown.scss'
import { useIntl } from 'react-intl'

interface EditableDropdownInputProps {
  options: string[]
  value: string
  onChange: (value: string) => void
}

const EditableDropdownInput: React.FC<EditableDropdownInputProps> = ({
  options,
  value,
  onChange,
}) => {
  const { formatMessage } = useIntl()
  const [showDropdown, setShowDropdown] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isEditable, setIsEditable] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setInputValue(value)
  }, [value]) // eslint-disable-line

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        handleInputBlur()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, []) // eslint-disable-line

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value)
    setError(null)
  }

  const handleOptionClick = (option: string) => {
    setInputValue(option)
    onChange(option)
    setShowDropdown(false)
  }

  const handleDoubleClick = () => {
    setIsEditable(true)
    setShowDropdown(false)
    inputRef.current?.focus()
  }

  const handleClick = () => {
    setShowDropdown((prev) => !prev)
  }

  const formatTime = (hours: number, minutes: number, period: string) => {
    let formattedHours = hours % 12 || 12 // Convert 0 to 12 for AM
    let formattedMinutes = minutes.toString().padStart(2, '0')
    return `${formattedHours.toString().padStart(2, '0')}:${formattedMinutes} ${period}`
  }

  const parseTime = (input: string) => {
    // Replace . with : and normalize input
    const normalizedInput = input.trim().toUpperCase().replace('.', ':')

    // Regex to match formats with or without minutes and with AM/PM
    const regex = /^(\d{1,2})(?::(\d{1,2}))?\s*(AM|PM)?$/
    const match = normalizedInput.match(regex)

    if (match) {
      let hours = parseInt(match[1], 10)
      let minutes = match[2] ? parseInt(match[2], 10) : 0
      let period = match[3] || ''

      // Normalize hours and period
      if (hours < 1 || hours > 12 || minutes < 0 || minutes > 59) {
        return null // Invalid hour or minute range
      }

      if (period && !['AM', 'PM'].includes(period)) {
        return null // Invalid period
      }

      if (!period) {
        // Default period based on hours
        period = hours >= 12 ? 'PM' : 'AM'
      }

      // Convert 24-hour to 12-hour format
      if (hours === 0) hours = 12
      if (hours > 12) hours -= 12

      // Handle single digit minute cases
      if (minutes < 10 && match[2]?.length === 1) {
        if (minutes < 6) {
          minutes *= 10 // e.g., 5:4 becomes 5:40
        } else {
          minutes = parseInt(`0${minutes}`, 10) // e.g., 5:8 becomes 5:08
        }
      }

      return formatTime(hours, minutes, period)
    }

    return null // Invalid format
  }

  const handleInputBlur = () => {
    if (inputValue.trim() === '') {
      return
    }

    const formattedTime = parseTime(inputValue)

    if (formattedTime) {
      setError(null)
      onChange(formattedTime)
    } else {
      setError('Invalid input.')
      setInputValue(value) // Reset to last valid value
    }

    setShowDropdown(false)
    setIsEditable(false)
  }

  return (
    <div className='editable-dropdown-input' style={{ position: 'relative' }} ref={containerRef}>
      <FormControl
        type='text'
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        onDoubleClick={handleDoubleClick}
        ref={inputRef}
        onClick={handleClick}
        readOnly={!isEditable}
        style={{
          borderColor: error ? 'red' : '',
          fontFamily: 'Poppins',
          fontWeight: 400,
          fontSize: '13px',
          color: '#383940',
          lineHeight: '20px',
        }}
      />

      {showDropdown && (
        <div
          className='dropdown-menu-scrollable'
          style={{
            position: 'absolute',
            fontFamily: 'Poppins',
            color: '#383940',
            fontWeight: 400,
            fontSize: '13px',
            lineHeight: '20px',
            zIndex: 1,
            bottom: '100%',
            width: '100%',
            maxHeight: '200px',
            overflowY: 'auto',
            backgroundColor: 'white',
            border: '1px solid #ced4da',
            borderRadius: '0.25rem',
            pointerEvents: 'auto', // Ensure pointer events are enabled
          }}
        >
          {options.length > 0 ? (
            options.map((option) => (
              <div
                key={option}
                onMouseDown={(e) => e.preventDefault()} // Prevent focus loss
                onClick={() => handleOptionClick(option)}
                className='editable-dropdown-item'
                style={{
                  height: '33px',
                  padding: '5px',
                  width: '100%',
                  cursor: 'pointer',
                }}
              >
                {formatMessage({ id: option })}
              </div>
            ))
          ) : (
            <div className='editable-dropdown-item'>{formatMessage({ id: 'No options available' })}</div>
          )}
        </div>
      )}
    </div>
  )
}

export default EditableDropdownInput
